import { Bot, Trash2, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { DraftMember } from './draft-member';

interface MemberListProps {
  members: DraftMember[];
  /** Optional — when omitted, the list is read-only (no trash icons). */
  onRemove?: (draftId: string) => void;
  /** Shown in place of the list when empty. */
  emptyMessage?: string;
}

/**
 * Renders the team member list as a bordered, divided list of rows.
 * Pure presentation — adds/removes happen through the parent's state via
 * `onRemove`. The editable/read-only split is one prop, not two components.
 */
export function MemberList({
  members,
  onRemove,
  emptyMessage = 'No members yet. Add at least one so this team can receive tasks.',
}: MemberListProps): React.ReactElement {
  if (members.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }
  return (
    <ul className="divide-y rounded-md border">
      {members.map((m) => (
        <li key={m.draftId} className="flex items-center gap-2 px-2 py-1.5 text-sm">
          {m.memberType === 'user' ? (
            <User className="h-4 w-4 text-muted-foreground" />
          ) : (
            <Bot className="h-4 w-4 text-muted-foreground" />
          )}
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm">{m.display.name || '?'}</span>
            {m.display.email && (
              <span className="truncate text-xs text-muted-foreground">{m.display.email}</span>
            )}
          </div>
          <Badge variant="outline" className="text-[10px]">
            {m.memberType}
          </Badge>
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              onClick={() => onRemove(m.draftId)}
              aria-label={`Remove ${m.display.name}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
